import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { adminService } from '../../services/admin';
import type { BillingStatus, Tenant } from '@traaker/shared';

const BILLING_COLORS: Record<BillingStatus, string> = {
  pending: '#8B9DC3',
  active: '#00DDB3',
  past_due: '#F59E0B',
  suspended: '#EF4444',
  cancelled: '#6B7280',
};

export default function AdminTenants() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    adminService
      .listTenants()
      .then(setTenants)
      .catch((err) => setError(err.message || 'Erreur de chargement'));
  }, []);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!tenants) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Admin — Tenants</Typography>
          <Typography variant="body2" color="text.secondary">
            {tenants.length} tenant{tenants.length > 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/tenants/new')}>
          Nouveau tenant
        </Button>
      </Box>

      <Card>
        <CardContent sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nom</TableCell>
                <TableCell>Domaine</TableCell>
                <TableCell>Tier</TableCell>
                <TableCell>Billing</TableCell>
                <TableCell>Mode</TableCell>
                <TableCell>Contrat</TableCell>
                <TableCell>Créé</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tenants.map((t) => (
                <TableRow
                  key={t.tenantId}
                  hover
                  onClick={() => navigate(`/admin/tenants/${t.tenantId}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>{t.name}</TableCell>
                  <TableCell>{t.domain}</TableCell>
                  <TableCell>
                    <Chip label={t.tier} size="small" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={t.billingStatus}
                      size="small"
                      sx={{
                        bgcolor: `${BILLING_COLORS[t.billingStatus]}20`,
                        color: BILLING_COLORS[t.billingStatus],
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>
                  <TableCell>{t.billingMode}</TableCell>
                  <TableCell>
                    {t.contractAmountCents != null
                      ? `${(t.contractAmountCents / 100).toFixed(0)} €/${t.contractInterval || 'month'}`
                      : '—'}
                  </TableCell>
                  <TableCell>{new Date(t.createdAt).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
              {tenants.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Aucun tenant
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
}
